import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/devLog";

/**
 * BusinessInfoContext — provides editable business info (contact, location,
 * hours, map) to Footer, Visit page, etc. Single fetch on mount, refreshable
 * after the Admin → Information tab saves.
 */
const BusinessInfoContext = createContext(null);

// Safe fallback so the UI never renders empty during the very first paint /
// network failure. These match the seeded backend defaults.
const FALLBACK = {
  name: "CTP Kenya",
  tagline: "Upload. Configure. Pay. Pick up.",
  phone: "0733 737 993",
  whatsapp: "0733 737 993",
  email: "help@ctpkenya.com",
  address_line1: "Tom Mboya Street, near Khoja Mosque",
  address_line2: "",
  city: "Nairobi CBD",
  country: "Kenya",
  maps_embed_url: "https://www.google.com/maps?q=Tom+Mboya+Street+Nairobi&output=embed",
  maps_embed_resolved: "https://www.google.com/maps?q=Tom+Mboya+Street+Nairobi&output=embed",
  maps_directions_url: "https://www.google.com/maps/dir/?api=1&destination=Tom+Mboya+Street+Nairobi",
  parking_info: "",
  matatu_info: "",
  hours: [
    { day: "Monday",    open: "08:00", close: "19:00", closed: false },
    { day: "Tuesday",   open: "08:00", close: "19:00", closed: false },
    { day: "Wednesday", open: "08:00", close: "19:00", closed: false },
    { day: "Thursday",  open: "08:00", close: "19:00", closed: false },
    { day: "Friday",    open: "08:00", close: "19:00", closed: false },
    { day: "Saturday",  open: "09:00", close: "17:00", closed: false },
    { day: "Sunday",    open: "",      close: "",      closed: true  },
  ],
};

export function BusinessInfoProvider({ children }) {
  const [info, setInfo] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/business/info");
      setInfo({ ...FALLBACK, ...data });
    } catch (err) {
      // keep fallback
      devLog.debug("business-info load failed, using fallback:", err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(
    () => ({ info, loaded, refresh }),
    [info, loaded, refresh]
  );

  return (
    <BusinessInfoContext.Provider value={value}>
      {children}
    </BusinessInfoContext.Provider>
  );
}

export const useBusinessInfo = () => useContext(BusinessInfoContext) || { info: FALLBACK, loaded: false, refresh: () => {} };
